<template>
  <div class="reward-extract-tab">
    <!-- Cabeçalho com resumo -->
    <b-row class="mb-0">
      <b-col cols="12">
        <b-card class="mb-0">
          <b-card-text>
            <b-row>
              <b-col
                cols="6"
                md="3"
              >
                <statistic-card-horizontal
                  :icon="'DollarSignIcon'"
                  :statistic="rewardSummary.currentBalance | currency"
                  statistic-title="Saldo Atual"
                  color="success"
                  :custom-style="{
                    border: '1px solid #28c76f33',
                    borderRadius: '8px',
                    backgroundColor: '#e6fffa33',
                  }"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
              >
                <statistic-card-horizontal
                  :icon="'AwardIcon'"
                  :statistic="rewardSummary.totalEarned | currency"
                  statistic-title="Total Ganho"
                  color="success"
                  :custom-style="{
                    border: '1px solid #28c76f33',
                    borderRadius: '8px',
                    backgroundColor: '#e6fffa33',
                  }"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
              >
                <statistic-card-horizontal
                  :icon="'SmileIcon'"
                  :statistic="rewardSummary.totalUsed | currency"
                  statistic-title="Total Utilizado"
                  color="warning"
                  :custom-style="{
                    border: '1px solid rgb(255 243 232)',
                    borderRadius: '8px',
                    backgroundColor: 'rgb(255 249 230)',
                  }"
                />
              </b-col>
              <b-col
                cols="6"
                md="3"
              >
                <statistic-card-horizontal
                  :icon="'ClockIcon'"
                  :statistic="rewardSummary.totalExpired | currency"
                  statistic-title="Total Expirado"
                  color="danger"
                  :custom-style="{
                    border: '1px solid rgb(252 234 234)',
                    borderRadius: '8px',
                    backgroundColor: 'rgb(255 244 246)',
                  }"
                />
              </b-col>
            </b-row>
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filtros -->
    <b-row class="mb-3">
      <b-col
        cols="12"
        md="4"
      >
        <FormulateInput
          v-model="filters.transactionType"
          :label="$t('Tipo de Transação')"
          :placeholder="$t('Todos')"
          type="vue-select"
          :options="transactionTypeOptions"
          @input="onFilterChange"
        />
      </b-col>
      <b-col
        cols="12"
        md="4"
      >
        <FormulateInput
          v-model="filters.startDate"
          :label="$t('Data Inicial')"
          :placeholder="$t('Todos')"
          type="datepicker"
          @input="onFilterChange"
        />
      </b-col>
      <b-col
        cols="12"
        md="4"
      >
        <FormulateInput
          v-model="filters.endDate"
          :label="$t('Data Final')"
          :placeholder="$t('Todos')"
          type="datepicker"
          @input="onFilterChange"
        />
      </b-col>
    </b-row>

    <!-- Tabela de transações -->
    <b-table
      :items="transactions"
      :fields="tableFields"
      :busy="loading"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      responsive
      striped
      hover
      show-empty
      empty-text="Nenhuma transação encontrada"
    >
      <template #table-busy>
        <div class="text-center text-muted my-2">
          <b-spinner class="align-middle mr-2" />
          <strong>Carregando transações...</strong>
        </div>
      </template>

      <template #cell(amount)="data">
        <span :class="getAmountClass(data.item.transactionType)">
          {{ data.value | currency }}
        </span>
      </template>

      <template #cell(expirationDate)="data">
        <span v-if="data.value">
          {{ data.value | date }}
          <b-badge
            v-if="data.item.isExpired"
            variant="danger"
            class="ml-1"
          > Expirado </b-badge>
          <b-badge
            v-else-if="isExpiringSoon(data.value)"
            variant="warning"
            class="ml-1"
          >
            Expira em breve
          </b-badge>
        </span>
        <span v-else>-</span>
      </template>
    </b-table>

    <!-- Paginação -->
    <b-row v-if="paging.rowCount > 0">
      <b-col
        cols="12"
        class="d-flex justify-content-between align-items-center"
      >
        <e-pagination-summary
          :current-page="paging.currentPage"
          :per-page="paging.pageSize"
          :total="paging.rowCount"
          :total-on-page="paging.rowsInCurrentPage"
        />
        <b-pagination
          v-model="paging.currentPage"
          :total-rows="paging.rowCount"
          :per-page="paging.pageSize"
          @change="onPageChange"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BRow, BCol, BCard, BCardText, BTable, BBadge, BSpinner, BPagination } from 'bootstrap-vue'
import moment from 'moment'
import { debounce } from 'lodash'
import { mapActions } from 'vuex'
import StatisticCardHorizontal from '@/@core/components/statistics-cards/StatisticCardHorizontal.vue'
import EPaginationSummary from '@/views/components/pagination/EPaginationSummary.vue'
import { sorting, pagination } from '@/mixins'

export default {
  name: 'RewardExtractTab',

  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
    StatisticCardHorizontal,
    EPaginationSummary,
  },

  mixins: [sorting, pagination],

  props: {
    customerId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      transactions: [],
      rewardSummary: {
        currentBalance: 0,
        totalEarned: 0,
        totalUsed: 0,
        totalExpired: 0,
      },
      sortBy: 'transactionDate',
      sortDesc: true,
      filters: {
        transactionType: '',
        startDate: null,
        endDate: null,
      },
      tableFields: [
        {
          key: 'transactionDate',
          label: this.$t('Data/Hora'),
          sortable: true,
          thStyle: { width: '150px' },
          tdClass: 'text-center',
          formatter: value => this.$options.filters.datetime(value),
        },
        {
          key: 'transactionType',
          label: this.$t('Tipo'),
          sortable: true,
          thStyle: { width: '200px' },
          tdClass: 'text-center',
          formatter: value => this.$t(`Rewards.TransactionType.${value}`),
        },
        {
          key: 'amount',
          label: this.$t('Valor'),
          sortable: true,
          thStyle: { width: '120px' },
        },
        {
          key: 'description',
          label: this.$t('Descrição'),
          sortable: false,
          formatter: (value, index, item) =>
            `${value}${item.transactionStatus === 'Pending' ? ` (${this.$t('Pendente')})` : ''}`,
        },
        {
          key: 'expirationDate',
          label: this.$t('Validade'),
          sortable: true,
          thStyle: { width: '120px' },
          tdClass: 'text-center',
        },
      ],
      transactionTypeOptions: [
        { value: '', label: 'Todos os tipos' },
        { value: 'CreditBySale', label: 'Crédito por venda' },
        { value: 'CreditManual', label: 'Crédito manual' },
        { value: 'DebitBySaleCancellation', label: 'Débito por cancelamento' },
        { value: 'DebitByCreditUsage', label: 'Débito por uso' },
        { value: 'DebitByExpiration', label: 'Débito por expiração' },
        { value: 'DebitManual', text: 'Débito manual' },
      ],
    }
  },

  watch: {
    customerId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.loadData()
        }
      },
    },
  },

  created() {
    this.debouncedSearch = debounce(this.loadTransactions, 500)
  },

  mounted() {
    this.paging.pageSize = 10
  },

  methods: {
    ...mapActions('reward', ['getRewardSummary', 'getRewardTransactions']),

    async loadData() {
      await Promise.all([this.loadSummary(), this.loadTransactions()])
    },

    async loadSummary() {
      try {
        this.rewardSummary = await this.getRewardSummary(this.customerId)
      } catch (error) {
        console.error('Erro ao carregar resumo de recompensas:', error)
      }
    },

    async loadTransactions() {
      try {
        this.loading = true

        const params = {
          pageIndex: this.paging.currentPage - 1,
          pageSize: this.paging.pageSize,
          sortBy: this.getSorting(this),
          ...this.filters,
        }

        const result = await this.getRewardTransactions({
          customerId: this.customerId,
          params,
        })

        this.transactions = result.results || []
        this.paging = {
          ...this.paging,
          rowCount: result.rowCount,
          rowsInCurrentPage: result.rowsInCurrentPage,
        }
      } catch (error) {
        console.error('Erro ao carregar transações de recompensas:', error)
        this.transactions = []
      } finally {
        this.loading = false
      }
    },

    onFilterChange() {
      this.paging.currentPage = 1
      this.debouncedSearch()
    },

    onPageChange(page) {
      this.paging.currentPage = page
      this.loadTransactions()
    },

    getTransactionTypeVariant(type) {
      const variants = {
        CreditBySale: 'success',
        CreditManual: 'info',
        DebitBySaleCancellation: 'warning',
        DebitByCreditUsage: 'primary',
        DebitByExpiration: 'danger',
        DebitManual: 'secondary',
      }
      return variants[type] || 'light'
    },

    getAmountClass(type) {
      return type?.startsWith('Credit') ? 'text-success font-weight-bold' : 'text-danger'
    },

    isExpiringSoon(date) {
      return moment(date, moment.ISO_8601).isBetween(moment(), moment().add(7, 'days'))
    },
  },
}
</script>

<style scoped>
.reward-extract-tab .table td {
  vertical-align: middle;
}

.reward-extract-tab .badge {
  font-size: 0.8em;
}
</style>
